import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import api from '../../api/api';

const initialState = {
  reviews: [],
  totalReviews: 0,
  pendingCount: 0,
  approvedCount: 0,
  rejectedCount: 0,
  currentPage: 1,
  totalPages: 1,
  stats: {
    totalReviews: 0,
    pendingReviews: 0,
    approvedReviews: 0,
    rejectedReviews: 0,
    averageRating: 0
  },
  loading: false,
  successMessage: '',
  errorMessage: ''
};

// Get seller reviews
export const get_seller_reviews = createAsyncThunk(
  'review/get_seller_reviews',
  async ({ sellerId, pageNo = 1, status = 'all' }, { rejectWithValue }) => {
    try {
      console.log('get_seller_reviews called with:', { sellerId, pageNo, status });
      const params = new URLSearchParams();
      params.append('pageNo', pageNo);
      params.append('status', status);
      
      const url = `/dashboard/reviews/seller/${sellerId}/reviews?${params.toString()}`;
      console.log('Making API call to:', url);
      
      const { data } = await api.get(url, {
        withCredentials: true
      });
      console.log('API response:', data);
      return data;
    } catch (error) {
      console.error('get_seller_reviews error:', error.response?.data || error.message);
      return rejectWithValue(error.response?.data || { error: 'Failed to fetch reviews' });
    }
  }
);

// Get review statistics
export const get_review_stats = createAsyncThunk(
  'review/get_review_stats',
  async ({ sellerId }, { rejectWithValue }) => {
    try {
      const { data } = await api.get(`/dashboard/reviews/seller/${sellerId}/review-stats`, {
        withCredentials: true
      });
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to fetch review statistics' });
    }
  }
);

// Approve review
export const approve_review = createAsyncThunk(
  'review/approve_review',
  async ({ reviewId, sellerId }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await api.put(`/dashboard/reviews/seller/reviews/${reviewId}/approve`, 
        { sellerId },
        { withCredentials: true }
      );
      
      // Refresh reviews after approval
      dispatch(get_seller_reviews({ sellerId, pageNo: 1, status: 'all' }));
      dispatch(get_review_stats({ sellerId }));
      
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to approve review' });
    }
  }
);

// Reject review
export const reject_review = createAsyncThunk(
  'review/reject_review',
  async ({ reviewId, sellerId }, { rejectWithValue, dispatch }) => {
    try {
      const { data } = await api.put(`/dashboard/reviews/seller/reviews/${reviewId}/reject`, 
        { sellerId },
        { withCredentials: true }
      );
      
      // Refresh reviews after rejection
      dispatch(get_seller_reviews({ sellerId, pageNo: 1, status: 'all' }));
      dispatch(get_review_stats({ sellerId }));
      
      return data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { error: 'Failed to reject review' });
    }
  }
);

const reviewSlice = createSlice({
  name: 'review',
  initialState,
  reducers: {
    clearMessages: (state) => {
      state.successMessage = '';
      state.errorMessage = '';
    },
    clearReviews: (state) => {
      state.reviews = [];
      state.totalReviews = 0;
      state.pendingCount = 0;
      state.approvedCount = 0;
      state.rejectedCount = 0;
      state.currentPage = 1;
      state.totalPages = 1;
    }
  },
  extraReducers: (builder) => {
    // Get seller reviews
    builder
      .addCase(get_seller_reviews.pending, (state) => {
        state.loading = true;
        state.errorMessage = '';
      })
      .addCase(get_seller_reviews.fulfilled, (state, action) => {
        state.loading = false;
        state.reviews = action.payload.reviews || [];
        state.totalReviews = action.payload.totalReviews || 0;
        state.pendingCount = action.payload.pendingCount || 0;
        state.approvedCount = action.payload.approvedCount || 0;
        state.rejectedCount = action.payload.rejectedCount || 0;
        state.currentPage = action.payload.currentPage || 1;
        state.totalPages = action.payload.totalPages || 1;
      })
      .addCase(get_seller_reviews.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload?.error || 'Failed to fetch reviews';
      });

    // Get review statistics
    builder
      .addCase(get_review_stats.pending, (state) => {
        state.errorMessage = '';
      })
      .addCase(get_review_stats.fulfilled, (state, action) => {
        state.stats = action.payload;
      })
      .addCase(get_review_stats.rejected, (state, action) => {
        state.errorMessage = action.payload?.error || 'Failed to fetch review statistics';
      });

    // Approve review
    builder
      .addCase(approve_review.pending, (state) => {
        state.loading = true;
        state.errorMessage = '';
      })
      .addCase(approve_review.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message || 'Review approved successfully';
      })
      .addCase(approve_review.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload?.error || 'Failed to approve review';
      });

    // Reject review
    builder
      .addCase(reject_review.pending, (state) => {
        state.loading = true;
        state.errorMessage = '';
      })
      .addCase(reject_review.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message || 'Review rejected successfully';
      })
      .addCase(reject_review.rejected, (state, action) => {
        state.loading = false;
        state.errorMessage = action.payload?.error || 'Failed to reject review';
      });
  }
});

export const { clearMessages, clearReviews } = reviewSlice.actions;
export default reviewSlice.reducer; 